import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface SubscriptionRow {
  id: string;
  user_id: string;
  provider: string | null;
  razorpay_subscription_id: string | null;
  razorpay_customer_id: string | null;
  razorpay_plan_id: string | null;
  plan_key: string | null;
  pending_plan_key: string | null;
  price_id: string | null;
  product_id: string | null;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  environment: string;
  currency: string | null;
  billing_cycle: string | null;
  amount: number | null;
}

const ACTIVE_STATUSES = new Set([
  "active", "authenticated", "trialing", "halted",
]);

/**
 * Canonical subscription read for the current user.
 * Returns the most recent row.
 */
export function useSubscription(userId?: string) {
  const [sub, setSub] = useState<SubscriptionRow | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!userId) { setSub(null); setLoading(false); return; }
    const { data } = await supabase
      .from("subscriptions")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setSub((data as unknown as SubscriptionRow) ?? null);
    setLoading(false);
  }, [userId]);

  useEffect(() => { void refetch(); }, [refetch]);

  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`subs-${userId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${userId}` }, () => { void refetch(); })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [userId, refetch]);

  const isActive =
    !!sub &&
    ((ACTIVE_STATUSES.has(sub.status) && !!sub.current_period_end && new Date(sub.current_period_end) > new Date()) ||
      (sub.status === "canceled" && sub.current_period_end && new Date(sub.current_period_end) > new Date()) ||
      (sub.status === "cancelled" && sub.current_period_end && new Date(sub.current_period_end) > new Date()));

  // Tier derivation: prefer plan_key, then fall back to legacy price_id.
  let tier: "free" | "circle" | "advisory_board" = "free";
  if (isActive && sub) {
    if (sub.plan_key?.startsWith("advisory_")) tier = "advisory_board";
    else if (sub.plan_key?.startsWith("circle_")) tier = "circle";
    else if (sub.price_id?.startsWith("advisory_")) tier = "advisory_board";
    else tier = "circle";
  }

  return { subscription: sub, isActive: !!isActive, tier, loading, refetch };
}
